import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import useClients from '../hooks/useClients';
import { createClient } from '../../supabase/clients';

const PAGE_SIZE = 25;

const TABS = ['All', 'Active', 'Inactive', 'Maintenance', 'Weekend'];

const statusColors = {
  active: 'bg-emerald-100 text-emerald-800',
  inactive: 'bg-gray-100 text-gray-600',
  pending: 'bg-yellow-100 text-yellow-800',
};

const initials = (name = '') =>
  name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

const ALLOWED_COUNTIES = ["St. Mary's County", "Charles County", "Calvert County"];
const isOutOfArea = (c) => {
  const county = c.personalInfo?.county || c.county || '';
  return county && !ALLOWED_COUNTIES.includes(county);
};

function AddClientModal({ onClose, onSave }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', type: 'weekend', status: 'active' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createClient(form);
      onSave();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="p-5 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 text-lg">Add Client</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {[
            { name: 'name', label: 'Full Name', type: 'text', required: true },
            { name: 'email', label: 'Email', type: 'email' },
            { name: 'phone', label: 'Phone', type: 'tel' },
          ].map(f => (
            <div key={f.name}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
              <input
                type={f.type}
                required={f.required}
                value={form[f.name]}
                onChange={e => setForm(p => ({ ...p, [f.name]: e.target.value }))}
                className="w-full bg-white text-gray-900 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
              />
            </div>
          ))}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Client Type</label>
            <select
              value={form.type}
              onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
            >
              <option value="weekend">Weekend (One-time)</option>
              <option value="maintenance">Maintenance Program</option>
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-200 rounded-lg py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#C9A84C] text-black rounded-lg py-2 text-sm font-semibold hover:bg-[#b8943d] disabled:opacity-50">
              {saving ? 'Saving…' : 'Add Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Clients() {
  const { clients, loading } = useClients();
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('All');
  const [sort, setSort] = useState('name');
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);

  const filtered = useMemo(() => {
    let list = [...clients];

    if (tab === 'Active') list = list.filter(c => c.status === 'active');
    else if (tab === 'Inactive') list = list.filter(c => c.status === 'inactive');
    else if (tab === 'Maintenance') list = list.filter(c => c.type === 'maintenance');
    else if (tab === 'Weekend') list = list.filter(c => c.type === 'weekend');

    if (search) {
      const s = search.toLowerCase();
      list = list.filter(c =>
        (c.name || '').toLowerCase().includes(s) ||
        (c.email || '').toLowerCase().includes(s) ||
        (c.phone || '').includes(s)
      );
    }

    list.sort((a, b) => {
      if (sort === 'name') return (a.name || '').localeCompare(b.name || '');
      if (sort === 'recent') return new Date(b.created_at) - new Date(a.created_at);
      if (sort === 'value') return (b.lifetime_value || 0) - (a.lifetime_value || 0);
      return 0;
    });

    return list;
  }, [clients, search, tab, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="p-6 pb-24 md:pb-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-gray-900">Clients</h2>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-[#C9A84C] text-black px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#b8943d]"
        >
          <Plus size={16} /> Add Client
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search name, email, phone…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 bg-white text-gray-900 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
          />
        </div>
        <select
          value={sort}
          onChange={e => setSort(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C] bg-white"
        >
          <option value="name">Sort: Name</option>
          <option value="recent">Sort: Newest</option>
          <option value="value">Sort: Lifetime Value</option>
        </select>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => { setTab(t); setPage(1); }}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === t
                ? 'border-[#C9A84C] text-[#C9A84C]'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C9A84C]" />
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Client</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Contact</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">Last Service</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 hidden xl:table-cell">Lifetime Value</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                      No clients found.
                    </td>
                  </tr>
                ) : (
                  paginated.map(c => (
                    <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <Link to={`/admin/clients/${c.id}`} className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#C9A84C] flex items-center justify-center text-black text-xs font-bold shrink-0">
                            {initials(c.name)}
                          </div>
                          <span className="font-medium text-gray-900 hover:text-[#C9A84C]">{c.name}</span>
                          {isOutOfArea(c) && (
                            <span className="text-[10px] bg-orange-100 text-orange-700 border border-orange-300 px-1.5 py-0.5 rounded-full font-semibold shrink-0">Out of Area</span>
                          )}
                        </Link>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-gray-600">
                        <div>{c.email}</div>
                        <div className="text-xs text-gray-400">{c.phone}</div>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          c.type === 'maintenance'
                            ? 'bg-yellow-100 text-yellow-800 border border-[#C9A84C]'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {c.type === 'maintenance' ? 'Maintenance' : 'Weekend'}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-gray-500 text-xs">
                        {c.last_service_date ? format(new Date(c.last_service_date), 'MMM d, yyyy') : '—'}
                      </td>
                      <td className="px-4 py-3 hidden xl:table-cell text-gray-700">
                        ${(c.lifetime_value || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[c.status] || 'bg-gray-100 text-gray-600'}`}>
                          {c.status || 'active'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>
              Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setPage(p => Math.min(pageCount, p + 1))}
                disabled={page === pageCount}
                className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </>
      )}

      {showAdd && <AddClientModal onClose={() => setShowAdd(false)} onSave={() => {}} />}
    </div>
  );
}
